/**
 * B2G Export Certificate → Typesense document builder.
 *
 * Reads from ec.certificate_request (B2G wine data) and builds a search
 * document for the export-certificates Typesense collection.
 * Supports { returnDoc: true } for the bulk load pipeline.
 *
 * This is the search-document-builders equivalent of @tc/b2g's sendECToSearch,
 * kept here so the bulk load pipeline can use it without depending on @tc/b2g.
 */
import {
  MessageAttributeValue,
  SQSClient,
  SendMessageCommand,
} from "@aws-sdk/client-sqs"
import {
  certificateRequest,
  certificateRequestRevision,
  certificateRequestStatus,
  customsPermitNumber,
} from "@tc/drizzle-v2"
import {
  B2GExportCertificateRequestInput,
  ExportCertificateStatus,
} from "@tc/graphql-server"
import { DbTransaction } from "@tc/middy-decorators"
import { country_code_to_market } from "@tc/countries"
import { eq, desc, sql, and, inArray } from "drizzle-orm"
import { truncateToNzDay } from "../utils/truncate-to-nz-day"
import { toEligibilityMarketLabel } from "../utils/eligibility-market-labels"
import { searchBuilderLog } from "../utils/logger"

const client = new SQSClient({})

const toCertificateNumberSort = (certificateNumber: string): string =>
  certificateNumber
    .split("/")
    .map((seg) => (/^\d+$/.test(seg) ? seg.padStart(6, "0") : seg))
    .join("/")

const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  RAISED: "Raised",
  CANCELLED: "Cancelled",
  AMENDED: "Amended",
  VERIFIED: "Verified",
  PROVISIONALLY_APPROVED: "Provisionally Approved",
  APPROVED: "Approved",
  ACKNOWLEDGED: "Acknowledged",
  ACCEPTED: "Accepted",
  REQUESTED_TO_REVOKE: "Requested to Revoke",
  REQUESTED_TO_RESUBMIT: "Requested to Resubmit",
  REQUESTED_TO_REPLACE: "Requested to Replace",
  PENDING_REPLACEMENT: "Pending Replacement",
  REVOKED: "Revoked",
  REPLACED: "Replaced",
  RESUBMITTED: "Resubmitted",
  REPLACEMENT_AUTHORISED: "Replacement Authorised",
}

const getLatestB2gExportRequest = async (tx: DbTransaction, id: string) => {
  const lastRevision = tx
    .select({ id: certificateRequestRevision.id })
    .from(certificateRequestRevision)
    .where(eq(certificateRequestRevision.certificateRequestId, id))
    .orderBy(desc(certificateRequestRevision.id))
    .limit(1)

  const lastStatus = tx
    .select({ id: certificateRequestStatus.id })
    .from(certificateRequestStatus)
    .innerJoin(
      certificateRequestRevision,
      eq(
        certificateRequestRevision.id,
        certificateRequestStatus.certificateRequestRevisionId,
      ),
    )
    .where(eq(certificateRequestRevision.certificateRequestId, id))
    .orderBy(desc(certificateRequestStatus.id))
    .limit(1)

  // Feature #178802: "submitted" date for Wine = the timestamp of the latest
  // submission-type status change. Per Business SME, both RAISED and AMENDED
  // count as submissions (re-submitting after amendment bumps the date). A
  // replacement is a separate request record with its own RAISED, so it does
  // not affect the original request's submitted date.
  const lastSubmittedAt = tx
    .select({ createdAt: certificateRequestStatus.createdAt })
    .from(certificateRequestStatus)
    .innerJoin(
      certificateRequestRevision,
      eq(
        certificateRequestRevision.id,
        certificateRequestStatus.certificateRequestRevisionId,
      ),
    )
    .where(
      and(
        eq(certificateRequestRevision.certificateRequestId, id),
        inArray(certificateRequestStatus.status, [
          ExportCertificateStatus.Raised,
          ExportCertificateStatus.Amended,
        ]),
      ),
    )
    .orderBy(desc(certificateRequestStatus.createdAt))
    .limit(1)

  const [row] = await tx
    .select({
      id: certificateRequestRevision.certificateRequestId,
      requestorPartyId: certificateRequest.requestorPartyId,
      request: certificateRequestRevision.request,
      modified_at: certificateRequestRevision.createdAt,
      certificateNumber: certificateRequest.certificateNumber,
      status: sql<string>`coalesce(certificate_request_status.status, 'DRAFT')`,
      submittedAt: sql<Date | null>`(${lastSubmittedAt})`,
      commodity: certificateRequestRevision.commodity,
      createdAt: certificateRequest.createdAt,
    })
    .from(certificateRequest)
    .innerJoin(
      certificateRequestRevision,
      eq(certificateRequestRevision.id, lastRevision),
    )
    .leftJoin(
      certificateRequestStatus,
      eq(certificateRequestStatus.id, lastStatus),
    )
    .leftJoin(
      customsPermitNumber,
      eq(customsPermitNumber.certificateRequestId, certificateRequest.id),
    )
    .where(eq(certificateRequest.id, id))

  return row
}

export const saveEcB2gToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName?: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const row = await getLatestB2gExportRequest(tx, id)
  if (!row) {
    searchBuilderLog.debug({ message: "B2G certificate request not found", id })
    return
  }

  const document = buildEcB2gDoc(id, row)
  if (!document) return

  if (options?.returnDoc) return document as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName ?? process.env["COLLECTION_NAME"],
      DataType: "String",
    },
  }

  await client.send(
    new SendMessageCommand({
      QueueUrl: process.env["QUEUE_URL"],
      MessageBody: JSON.stringify(document),
      MessageAttributes: attributes,
    }),
  )
}

type B2gRow = NonNullable<Awaited<ReturnType<typeof getLatestB2gExportRequest>>>

const buildEcB2gDoc = (id: string, row: B2gRow) => {
  const requestBody = row.request as B2GExportCertificateRequestInput
  if (!requestBody) return undefined

  const organisationName = requestBody.requestedOnBehalfOfConsignor
    ? requestBody.consignment?.consignor?.specifiedPerson?.name
    : requestBody.consignment?.consignor?.name

  const modified_ts = new Date(row.modified_at.toString()).getTime()
  let departure_ts = 0
  if (requestBody.consignment?.exportExitDateTime) {
    departure_ts = new Date(
      requestBody.consignment.exportExitDateTime,
    ).getTime()
  }

  const items = requestBody.consignment?.items ?? []

  const products = items.map((item) => ({
    wine_name: item.product?.wine?.package?.nameForDocuments ?? "",
    net_volume: item.product?.netVolume ?? "",
    package_quantity: item.product?.physicalPackage?.quantity ?? "",
    package_type: item.product?.physicalPackage?.typeCode?.name ?? "",
    exporter_reference: item.product?.exporterReference ?? "",
    description: item.product?.description ?? "",
    net_weight: item.product?.netWeight ?? "",
  }))

  // Flat searchable attributes (Feature #178802) — mirror the AP export
  // certificate builder so Wine requests are discoverable by product,
  // transport and consignment information in the DFE "View all requests" page.
  const dedupe = (values: (string | null | undefined)[]): string[] => [
    ...new Set(values.filter((v): v is string => Boolean(v && v.trim()))),
  ]

  const containers = requestBody.consignment?.containers ?? []

  const productDescriptions = dedupe(
    items.map((item) => item.product?.description),
  )

  const productEligibilityCountries = dedupe(
    items.flatMap((item) =>
      (item.product?.declarations ?? []).map((d) => d.market),
    ),
  )

  const productEligibilityCountryLabels = productEligibilityCountries.map(
    toEligibilityMarketLabel,
  )

  const productContainerNumbers = dedupe(
    containers.map((container) => container.containerId),
  )

  const productSeals = dedupe(
    containers.flatMap((container) =>
      (container.affixedSeals ?? []).map((seal) => seal.sealId),
    ),
  )

  // The "Vessel" search field must use the
  // "Airwaybill/Vessel voyage number" (mainCarriageTransportMovements[0].reference),
  // NOT the freight forwarder name (consignment.carrier.name).
  const vessel =
    requestBody.consignment?.mainCarriageTransportMovements?.[0]?.reference ??
    ""

  const destinationMarket =
    requestBody.certificateType && requestBody.destination?.id
      ? country_code_to_market(
          requestBody.certificateType,
          requestBody.destination.id,
        )
      : undefined

  const transportMode =
    requestBody.consignment?.mainCarriageTransportMovements?.[0]?.mode ?? ""

  return {
    id,
    party_id: row.requestorPartyId,
    country_id: requestBody.destination?.id ?? "",
    country_name: requestBody.destination?.name ?? "",
    request_name: requestBody.requestName ?? "",
    status: row.status,
    status_label: STATUS_LABELS[row.status] ?? "",
    certificate_number: row.certificateNumber ?? "",
    certificate_number_sort: toCertificateNumberSort(
      row.certificateNumber ?? "",
    ),
    departure_date:
      departure_ts > 0
        ? new Date(departure_ts).toString().replace(/\s*\(.*\)$/, "")
        : "",
    modified_ts,
    modified_at: row.modified_at.toString().replace(/\s*\(.*\)$/, ""),
    departure_ts,
    organisation: organisationName ?? "",
    accessible_by: [row.requestorPartyId],
    commodity: row.commodity ?? "",
    certificate_type: requestBody.certificateType ?? "",
    verifying_agency: requestBody.consignment?.verifier?.name ?? "",
    carrier_name: requestBody.consignment?.carrier?.name ?? "",
    destination_market: destinationMarket,
    order_number: requestBody.consignment?.consignorReference ?? "",
    consignee_name: requestBody.consignment?.consignee?.name ?? "",
    consignee_country:
      requestBody.consignment?.consignee?.specifiedAddress?.countryName ?? "",
    exporter_country:
      requestBody.consignment?.consignor?.specifiedAddress?.countryName ?? "",
    first_consignee_name: requestBody.consignment?.delivery?.name ?? "",
    exported_by_another_party:
      requestBody.requestedOnBehalfOfConsignor ?? false,
    format_for_shipping:
      requestBody.consignment?.wineConsignment?.shippingFormat ?? "",
    total_cases:
      requestBody.consignment?.wineConsignment?.packaging?.quantity ?? "",
    created_at: row.createdAt.toString().replace(/\s*\(.*\)$/, ""),
    created_ts: new Date(row.createdAt).getTime(),
    destination_country_name: requestBody.destination?.name ?? "",
    transport_mode: transportMode,
    product: products,
    product_descriptions: productDescriptions,
    product_eligibility_countries: productEligibilityCountries,
    product_eligibility_country_labels: productEligibilityCountryLabels,
    product_container_numbers: productContainerNumbers,
    product_seals: productSeals,
    vessel,
    // Feature #178802: Wine has no dedicated UCR field. Per Business SME, the
    // free-text request name serves as the Unique Consignment Reference in the
    // Wine context, so index it here too (full-match behaviour is enforced by
    // the frontend query config for unique_consignment_ref).
    unique_consignment_ref: requestBody.requestName ?? "",
    // Feature #178802: submitted date = latest RAISED/AMENDED status timestamp,
    // truncated to NZ day to match the AP submitted_at facet behaviour.
    submitted_at: row.submittedAt
      ? truncateToNzDay(new Date(row.submittedAt.toString()).getTime())
      : null,
  }
}

export const getEcB2gBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, unknown>> => {
  if (ids.length === 0) return new Map()

  // Reuse the existing per-ID query which correctly resolves
  // latest revision → latest status for that revision via correlated subqueries.
  // Run in parallel batches for throughput.
  const CONCURRENCY = 50
  const result = new Map<string, unknown>()

  for (let i = 0; i < ids.length; i += CONCURRENCY) {
    const batch = ids.slice(i, i + CONCURRENCY)
    const rows = await Promise.all(
      batch.map(async (id) => {
        const row = await getLatestB2gExportRequest(tx, id)
        return { id, row }
      }),
    )
    for (const { id, row } of rows) {
      if (!row) continue
      const doc = buildEcB2gDoc(id, row)
      if (doc) result.set(id, doc)
    }
  }

  return result
}
